import axios from "axios";

/**
 * QR Loyalty & Ordering — Cashier page API client.
 *
 * Like the kitchen client, a standalone PIN-gated surface (no dashboard session):
 * the venue PIN is swapped for a scoped token (KitchenTokenService) sent as a
 * Bearer on the guarded board/pay calls, reaching ONLY the cashier routes. Same
 * baseURL (…/api) as the rest of the app. Points are awarded on payment.
 */
const client = axios.create({
  baseURL: process.env.REACT_APP_NOT_SECRET_CODE,
  withCredentials: false,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
});

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/** POST the PIN → { token, expires_in, company_id, venue_name, currency_symbol, orders[], revenue }. */
export const openCashierSession = (venueCode, pin) =>
  client.post(`/loyalty/cashier/session`, { venue_code: venueCode, pin }).then((r) => r.data);

/**
 * Today's board snapshot → { orders[], revenue, rewards[], checkins[], celebrations[] }.
 * ONE polled REST call for the whole till, deliberately not a second real-time
 * channel: everything on screen then comes out of the same request and cannot
 * disagree with itself.
 */
export const getCashierBoard = (token) =>
  client.get(`/loyalty/cashier/board`, auth(token)).then((r) => r.data);

/**
 * Mark an order paid (awards points) → { ok, order, ...board }.
 * Returns the WHOLE refreshed board: paying is the one action that credits
 * points, opens the redemption gate and can raise a milestone all at once, and
 * a cheer that waits five seconds for the next poll has already missed.
 */
export const payOrder = (token, id) =>
  client.post(`/loyalty/cashier/orders/${encodeURIComponent(id)}/pay`, {}, auth(token)).then((r) => r.data);

/* ── Manual points — purchases made OUTSIDE the QR order flow ────────────────
 * The company is taken from the scoped token server-side, so none of these
 * carry a venue id. Search matches name OR phone, with the phone canonicalised
 * against the venue's country before matching. */

/** Type-ahead customer search → { customers: [{ id, name, phone, visit_count, balance }] }. */
export const searchCustomers = (token, q, signal) =>
  client.get(`/loyalty/cashier/customers`, { ...auth(token), params: { q }, signal }).then((r) => r.data);

/** Quick-add a customer (phone canonicalised; an existing match is reused) → { customer }. */
export const createCustomer = (token, name, phone) =>
  client.post(`/loyalty/cashier/customers`, { name, phone }, auth(token)).then((r) => r.data);

/**
 * Award points for a keyed-in purchase amount → { ok, awarded, points, balance, customer }.
 * `idempotencyKey` must stay STABLE across retries of the same award and change
 * only for a genuinely new one — it is what stops a double-tap paying twice.
 * `force` re-submits past a 409 duplicate_recent challenge.
 */
export const awardPoints = (token, { customerId, amount, idempotencyKey, force = false }) =>
  client.post(`/loyalty/cashier/award`, {
    venue_customer_id: customerId,
    amount,
    idempotency_key: idempotencyKey,
    force,
  }, auth(token)).then((r) => r.data);

/* ── Redeeming — a TILL action, never a guest one ────────────────────────────
 * The customer card is view-only by construction; that property is the entire
 * reason a signed token in a URL is acceptable, so redemption lives here. */

/**
 * Burn points for a reward → { ok, charged, reward, points, customer }.
 *
 * `idempotencyKey` is REQUIRED and must be one key per redemption, stable
 * across retries. Without it the server falls back to the reward id, which
 * makes a reward redeemable once per customer FOREVER — the second redemption
 * silently returns the first one's ledger row and charges nothing (F-16).
 * `charged: false` means this key had already been used: say "already
 * redeemed", never imply a second burn.
 */
export const redeemReward = (token, { customerId, rewardId, idempotencyKey }) =>
  client.post(`/loyalty/cashier/redeem`, {
    customer_id: customerId,
    reward_id: rewardId,
    idempotency_key: idempotencyKey,
  }, auth(token)).then((r) => r.data);

/* ── Milestone cheers raised at Mark-paid ────────────────────────────────── */

/** Fire the TV celebration for a milestone → { ok, status, note }. */
export const celebrateMilestone = (token, id) =>
  client.post(`/loyalty/cashier/celebrations/${encodeURIComponent(id)}`, {}, auth(token)).then((r) => r.data);

/** "Not now" — resolves the cheer so the banner clears and never re-offers. */
export const dismissCelebration = (token, id) =>
  client.delete(`/loyalty/cashier/celebrations/${encodeURIComponent(id)}`, auth(token)).then((r) => r.data);

export default client;
